/**
 * Convert a persisted Odino transcript into display turns when REOPENING a
 * conversation from history (ADR-0024).
 *
 * The stored shape (`OdinoStoredMessage[]`) mirrors the agent loop: a user prompt,
 * then an assistant turn (text + tool_use blocks), then a user message carrying the
 * tool_result blocks fed back, repeat. For a readable reopened view we:
 *  - render a user PROMPT as a user bubble;
 *  - render an assistant turn as an assistant bubble with its text + tool chips
 *    (historical tools show as done — "ok");
 *  - DROP the internal tool_result messages (plumbing, not conversation) and any
 *    empty turn.
 * Thinking blocks never reach here (they're not persisted), so there's nothing to
 * strip for them.
 */
import type { OdinoSessionSummary, OdinoStoredMessage } from "./api-odino";
import type { TurnItem } from "./odinoTurn";

export interface HistoryTurn {
  role: "user" | "assistant";
  /** user bubble body */
  text?: string;
  /** assistant turn — text segments + tool chips, in order */
  items?: TurnItem[];
}

export function transcriptToTurns(messages: OdinoStoredMessage[]): HistoryTurn[] {
  const out: HistoryTurn[] = [];
  for (const m of messages) {
    if (m.role === "user") {
      const text = m.content
        .filter((b): b is Extract<typeof b, { t: "text" }> => b.t === "text")
        .map((b) => b.text)
        .join("")
        .trim();
      // A user message with no text is the tool_result plumbing — skip it.
      if (text) out.push({ role: "user", text });
      continue;
    }
    // Assistant: keep prose + tool chips in stream order; tools are historical → ok.
    const items: TurnItem[] = [];
    for (const b of m.content) {
      if (b.t === "text") {
        if (b.text) items.push({ kind: "text", text: b.text });
      } else if (b.t === "tool_use") {
        items.push({ kind: "tool", name: b.name, status: "ok" });
      }
    }
    if (items.length) out.push({ role: "assistant", items });
  }
  return out;
}

// ---------------------------------------------------------------------------
// History list: search + date grouping (scannable with many conversations)
// ---------------------------------------------------------------------------

export interface SessionGroup {
  /** i18n-free bucket label, ready to render. */
  label: string;
  items: OdinoSessionSummary[];
}

/** Case-insensitive title filter. Empty query → unchanged. */
export function filterSessions(
  sessions: OdinoSessionSummary[],
  query: string,
): OdinoSessionSummary[] {
  const q = query.trim().toLowerCase();
  if (!q) return sessions;
  return sessions.filter((s) => (s.title ?? "").toLowerCase().includes(q));
}

/** Bucket sessions by `updated_at` into Oggi / Ieri / Ultimi 7 giorni / Più
 *  vecchie (local calendar days). Input order is preserved within a group (the
 *  backend already returns most-recent-first); only non-empty groups are
 *  returned, in fixed order. `nowMs` is injected for testability. */
export function groupSessionsByDate(
  sessions: OdinoSessionSummary[],
  nowMs: number,
): SessionGroup[] {
  const startOfToday = new Date(nowMs);
  startOfToday.setHours(0, 0, 0, 0);
  const todayMs = startOfToday.getTime();
  const yesterdayMs = todayMs - 86_400_000;
  const weekMs = todayMs - 7 * 86_400_000;

  const oggi: OdinoSessionSummary[] = [];
  const ieri: OdinoSessionSummary[] = [];
  const settimana: OdinoSessionSummary[] = [];
  const vecchie: OdinoSessionSummary[] = [];

  for (const s of sessions) {
    const t = s.updated_at ? Date.parse(s.updated_at) : NaN;
    if (Number.isNaN(t) || t < weekMs) vecchie.push(s);
    else if (t >= todayMs) oggi.push(s);
    else if (t >= yesterdayMs) ieri.push(s);
    else settimana.push(s);
  }

  return [
    { label: "Oggi", items: oggi },
    { label: "Ieri", items: ieri },
    { label: "Ultimi 7 giorni", items: settimana },
    { label: "Più vecchie", items: vecchie },
  ].filter((g) => g.items.length > 0);
}
